// forms/html_element_helpers/error_popover.js

import { default_selectors } from '../../helpers/static_vars';

export const generate_random_error_popover_id = () =>
    `error-popover-${Math.random().toString(36).substring(2, 11)}`;

// Single, self-contained stylesheet for the error toast. All selectors are
// anchored to `.data-hub-error-popover-container` so they can never match host
// page elements. No generic utility classes, no unscoped element selectors, and
// no broad reset outside our own subtree.
//
// Why this ships in JS instead of relying on Tailwind: the toast is rendered in
// document.body, not the shadow root, so it can escape any transformed ancestor
// that would otherwise capture `position: fixed`.
const ERROR_POPOVER_STYLES_ATTRIBUTE = 'data-hub-error-popover-styles';
const ERROR_POPOVER_STYLES_CSS = `
.data-hub-error-popover-container {
    position: fixed;
    top: 1rem;
    right: 1rem;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.75rem;
    padding: 1rem;
    margin: 0;
    pointer-events: none;
    z-index: 2147483647;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #b91c1c;
}
.data-hub-error-popover-container,
.data-hub-error-popover-container * {
    box-sizing: border-box;
}
.data-hub-error-popover-container .data-hub-error-popover {
    pointer-events: auto;
}
.data-hub-error-popover-container .data-hub-error-popover-card {
    display: flex;
    align-items: flex-start;
    width: 300px;
    max-width: 90vw;
    padding: 0.75rem 1rem;
    background-color: #fee2e2;
    border: 1px solid #f87171;
    border-radius: 0.25rem;
    color: #b91c1c;
}
.data-hub-error-popover-container .data-hub-error-popover-body {
    flex: 1 1 auto;
    min-width: 0;
}
.data-hub-error-popover-container .data-hub-error-popover-title {
    margin: 0 0 0.25rem 0;
    font-weight: 700;
    font-size: 0.875rem;
    line-height: 1.25;
}
.data-hub-error-popover-container .data-hub-error-popover-message {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.25;
    word-wrap: break-word;
}
.data-hub-error-popover-container .data-hub-error-popover-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 0.5rem;
    cursor: pointer;
    background: none;
    border: 0;
    padding: 0;
}
.data-hub-error-popover-container .data-hub-error-popover-close-icon {
    width: 1.5rem;
    height: 1.5rem;
    display: block;
}
`;

/**
 * Inject our toast stylesheet into document.head exactly once per page.
 * @returns {void}
 */
const ensure_error_popover_styles_injected = () => {
    if (typeof document === 'undefined' || !document.head) {
        return;
    }
    if (document.head.querySelector(`style[${ERROR_POPOVER_STYLES_ATTRIBUTE}]`)) {
        return;
    }
    const style = document.createElement('style');
    style.setAttribute(ERROR_POPOVER_STYLES_ATTRIBUTE, '');
    style.type = 'text/css';
    style.textContent = ERROR_POPOVER_STYLES_CSS;
    document.head.appendChild(style);
};

/**
 * Creates an error popover container to hold error notifications. Always anchors
 * the container in document.body. The `parent` argument is kept only for API
 * compatibility with the old Shadow DOM implementation.
 *
 * The toast is `position: fixed`. If it is rendered inside an ancestor with
 * transform, filter, perspective, or will-change, that ancestor can become the
 * fixed-position containing block and the toast can paint off-screen. Mounting
 * in document.body avoids that class of host-page layout issue.
 *
 * @param {HTMLElement} _parent - Ignored; kept for API compatibility.
 * @param {String} error_container_selector - The selector to find the container.
 * @returns {HTMLElement|null} The error popover container element.
 */
export const createErrorPopoverContainer = (
    _parent = document.body,
    error_container_selector = default_selectors.error_container_selector
) => {
    ensure_error_popover_styles_injected();
    const body = typeof document !== 'undefined' ? document.body : null;
    if (!body) {
        return null;
    }
    let container = body.querySelector(error_container_selector);
    if (!container) {
        container = document.createElement('div');
        container.className = 'data-hub-error-popover-container';
        body.appendChild(container);
    }
    return container;
};

/**
 * Close an error popover.
 * @param {Event} event - The event that triggered the close.
 * @param {HTMLElement} this_element - The element to close.
 * @returns {void}
 */
export const close_error_popover = (event = null, this_element = null) => {
    if (event) {
        event.preventDefault();
        // Find the closest error popover if the event target is a child element
        if (!this_element) {
            this_element = event.target.closest(default_selectors.error_popover_selector);
        }
    }

    if (this_element) {

        // Add fade-out animation
        this_element.style.opacity = '0';
        this_element.style.transform = 'translateX(20px)';

        // Remove after animation completes. Guard against the element having
        // been detached while the timer was pending.
        setTimeout(() => {
            if (this_element.isConnected) {
                this_element.remove();
            }
        }, 300);
    } else {
        // Find the nearest element with the error_popover class
        const error_popover = event.target.closest(default_selectors.error_popover_selector);
        if (error_popover) {
            close_error_popover(null, error_popover);
        }

    }
};

/**
 * Finds or creates the document-level error popover container.
 * @param {String|HTMLElement} error_container_selector - Selector or explicit container element.
 * @param {HTMLElement|Document|ShadowRoot} parent_container - Ignored; kept for API compatibility.
 * @returns {HTMLElement|null}
 */
export const find_or_create_error_popover_container = (
    error_container_selector = default_selectors.error_container_selector,
    parent_container = document
) => {
    void parent_container;

    if (typeof document === 'undefined' || !document.body) {
        return null;
    }
    let error_popover_container = null;
    try {
        if (error_container_selector instanceof HTMLElement) {
            error_popover_container = error_container_selector;
        } else if (typeof error_container_selector === 'string') {
            if (error_container_selector.startsWith('#')) {
                error_popover_container = document.getElementById(error_container_selector.slice(1));
            } else {
                error_popover_container = document.body.querySelector(error_container_selector);
            }
        }
    } catch (error) {
        window?.data_hub?.error_function?.('Error finding return error container: ' + error, null);
    }

    // If error_popover_container is null create a new one
    if (!error_popover_container) {
        error_popover_container = createErrorPopoverContainer(document.body, error_container_selector);
    }

    return error_popover_container;
};

/**
 * Trigger a Toast-like error notification
 * @param {String} external_error_message to display to the user
 * @param {HTMLElement} parent_container of the target element to append the error popover
 * @param {String|HTMLElement} error_container_selector to find or create the error container
 * @returns {HTMLElement|undefined} error_popover
 */
export const show_error_popover = (
    external_error_message = "Something went wrong please try again later.",
    parent_container = document,
    error_container_selector = default_selectors.error_container_selector
) => {

    if (!external_error_message || external_error_message === '' || typeof external_error_message !== 'string') {
        return;
    }

    // Ensure the container exists
    const container = find_or_create_error_popover_container(error_container_selector, parent_container);

    // If no container still return an error
    if (!container || !(container instanceof HTMLElement)) {
        window?.data_hub?.error_function?.('Error finding or creating error container: ' + error_container_selector, null);
        return alert(external_error_message || 'An error occurred.');
    }

    // Generate a unique ID for the new error popover
    const error_popover = generate_error_popover_div({
        parent_container: container,
        external_error_message: external_error_message,
        id: generate_random_error_popover_id()
    });

    return error_popover;
};

/**
 * Generate an error popover div
 * @param {Object} params - Named parameters.
 * @param {HTMLElement} params.parent_container - The parent container to append the error popover to.
 * @param {String} params.external_error_message - The error message shown to the visitor.
 * @param {String} params.id - The unique ID for the error popover.
 * @returns {HTMLElement} error_popover
 */
export const generate_error_popover_div = ({
    parent_container = null,
    external_error_message = "Something went wrong please try again later.",
    id = generate_random_error_popover_id()
} = {}) => {

    // Create the container if it doesn't exist
    if (!parent_container) {
        parent_container = find_or_create_error_popover_container();
    }

    // Markup uses only `.data-hub-error-popover-*` classes. Every selector in
    // the injected stylesheet is anchored to .data-hub-error-popover-container,
    // so this DOM subtree is self-contained.
    const error_popover_div = document.createElement('div');
    // Static markup only — no interpolation of caller-supplied text into innerHTML.
    // The visitor-facing message is set via textContent below so an error string that
    // happens to contain HTML (e.g. relayed server/validation text) can never inject markup.
    error_popover_div.innerHTML = `
  <div id="${id}" class="data-hub-error-popover">
    <div class="data-hub-error-popover-card" role="alert">
      <div class="data-hub-error-popover-body">
        <p class="data-hub-error-popover-title">Error!</p>
        <p class="data-hub-error-popover-message data_hub_error_message"></p>
      </div>
      <span class="data-hub-error-popover-close" aria-label="Close error popover">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
             fill="none" stroke="#ef4444" stroke-width="1.5"
             class="data-hub-error-popover-close-icon">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </span>
    </div>
  </div>
  `;

    const error_message_element = error_popover_div.querySelector('.data_hub_error_message');
    if (error_message_element) {
        error_message_element.textContent = external_error_message;
    }

    // Initial state for animation
    error_popover_div.style.opacity = '0';
    error_popover_div.style.transform = 'translateX(-20px)';
    error_popover_div.style.transition = 'opacity 300ms ease-in-out, transform 300ms ease-in-out';

    parent_container.appendChild(error_popover_div);

    // Attach click event to close the popover
    error_popover_div.addEventListener('click', (event) => close_error_popover(event, error_popover_div));

    // Animate in. Skip if the popover got detached before the timer fired.
    setTimeout(() => {
        if (!error_popover_div.isConnected) {
            return;
        }
        error_popover_div.style.opacity = '1';
        error_popover_div.style.transform = 'translateX(0)';
    }, 10);

    // Auto-close after timeout. Same detach guard.
    setTimeout(() => {
        if (!error_popover_div.isConnected) {
            return;
        }
        close_error_popover(null, error_popover_div);
    }, 5000);

    return error_popover_div;
};

// ── Success toast ─────────────────────────────────────────────────────────────
// Green sibling of the error toast. Same design invariants: self-contained scoped
// stylesheet, mounted in document.body (escapes the shadow root + any transformed
// ancestor), position: fixed top-right. Success toasts stack in their own container
// so they can coexist with an error toast. Reuses close_error_popover (element-based).
const SUCCESS_POPOVER_STYLES_ATTRIBUTE = 'data-hub-success-popover-styles';
const SUCCESS_POPOVER_STYLES_CSS = `
.data-hub-success-popover-container {
    position: fixed;
    top: 1rem;
    right: 1rem;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.75rem;
    padding: 1rem;
    margin: 0;
    pointer-events: none;
    z-index: 2147483647;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #15803d;
}
.data-hub-success-popover-container,
.data-hub-success-popover-container * {
    box-sizing: border-box;
}
.data-hub-success-popover-container .data-hub-success-popover {
    pointer-events: auto;
}
.data-hub-success-popover-container .data-hub-success-popover-card {
    display: flex;
    align-items: flex-start;
    width: 300px;
    max-width: 90vw;
    padding: 0.75rem 1rem;
    background-color: #dcfce7;
    border: 1px solid #4ade80;
    border-radius: 0.25rem;
    color: #15803d;
}
.data-hub-success-popover-container .data-hub-success-popover-body { flex: 1 1 auto; min-width: 0; }
.data-hub-success-popover-container .data-hub-success-popover-title {
    margin: 0 0 0.25rem 0; font-weight: 700; font-size: 0.875rem; line-height: 1.25;
}
.data-hub-success-popover-container .data-hub-success-popover-message {
    margin: 0; font-size: 0.875rem; line-height: 1.25; word-wrap: break-word;
}
.data-hub-success-popover-container .data-hub-success-popover-close {
    display: inline-flex; align-items: center; justify-content: center;
    margin-left: 0.5rem; cursor: pointer; background: none; border: 0; padding: 0;
}
.data-hub-success-popover-container .data-hub-success-popover-close-icon { width: 1.5rem; height: 1.5rem; display: block; }
`;

const ensure_success_popover_styles_injected = () => {
    if (typeof document === 'undefined' || !document.head) {
        return;
    }
    if (document.head.querySelector(`style[${SUCCESS_POPOVER_STYLES_ATTRIBUTE}]`)) {
        return;
    }
    const style = document.createElement('style');
    style.setAttribute(SUCCESS_POPOVER_STYLES_ATTRIBUTE, '');
    style.type = 'text/css';
    style.textContent = SUCCESS_POPOVER_STYLES_CSS;
    document.head.appendChild(style);
};

const create_success_popover_container = () => {
    ensure_success_popover_styles_injected();
    const body = typeof document !== 'undefined' ? document.body : null;
    if (!body) {
        return null;
    }
    let container = body.querySelector('.data-hub-success-popover-container');
    if (!container) {
        container = document.createElement('div');
        container.className = 'data-hub-success-popover-container';
        body.appendChild(container);
    }
    return container;
};

/**
 * Trigger a Toast-like success notification (green). Mounted top-right on document.body so it
 * escapes the form's shadow root. Auto-dismisses; click to close early. No-op for an empty message.
 *
 * @param {String} external_success_message - Visitor-facing confirmation text.
 * @returns {HTMLElement|null} The success popover element, or null when nothing was shown.
 */
export const show_success_popover = (
    external_success_message = 'Thanks — your submission was received.'
) => {
    if (!external_success_message || typeof external_success_message !== 'string') {
        return null;
    }
    const container = create_success_popover_container();
    if (!container || !(container instanceof HTMLElement)) {
        return null;
    }

    const id = `success-popover-${Math.random().toString(36).substring(2, 11)}`;
    const success_popover_div = document.createElement('div');
    // Static markup; the visitor-facing message is set via textContent below (never innerHTML)
    // so relayed text that happens to contain HTML can't inject markup.
    success_popover_div.innerHTML = `
  <div id="${id}" class="data-hub-success-popover">
    <div class="data-hub-success-popover-card" role="status">
      <div class="data-hub-success-popover-body">
        <p class="data-hub-success-popover-title">Success!</p>
        <p class="data-hub-success-popover-message data_hub_success_message"></p>
      </div>
      <span class="data-hub-success-popover-close" aria-label="Close success popover">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
             fill="none" stroke="#16a34a" stroke-width="1.5"
             class="data-hub-success-popover-close-icon">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </span>
    </div>
  </div>
  `;

    const message_element = success_popover_div.querySelector('.data_hub_success_message');
    if (message_element) {
        message_element.textContent = external_success_message;
    }

    success_popover_div.style.opacity = '0';
    success_popover_div.style.transform = 'translateX(-20px)';
    success_popover_div.style.transition = 'opacity 300ms ease-in-out, transform 300ms ease-in-out';

    container.appendChild(success_popover_div);

    // Click anywhere on the toast closes it. Reuses the error close (element-based path).
    success_popover_div.addEventListener('click', () => close_error_popover(null, success_popover_div));

    setTimeout(() => {
        if (!success_popover_div.isConnected) {
            return;
        }
        success_popover_div.style.opacity = '1';
        success_popover_div.style.transform = 'translateX(0)';
    }, 10);

    setTimeout(() => {
        if (!success_popover_div.isConnected) {
            return;
        }
        close_error_popover(null, success_popover_div);
    }, 5000);

    return success_popover_div;
};
